/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ContractStatusEnum } from '../../../enums/contract-status.enum';
import { DeviceType } from '../../../enums/device-type.enum';
import { DialogContentType } from '../../../enums/dialog-type.enum';
import { PaymentMethodEnums, PaymentSourceEnums, PaymentTransactionType } from '../../../enums/payment-method.enum';
import { CreateTransactionResponse } from '../../../payment/models/create-transaction-response-model';
import { PaymentData, PaymentMetaData, PaymentMinMaxBoundary } from '../../../payment/models/payment-data.model';
import { PaymentAppPayOutput, TransactionResultEnum, VerifyTransaction } from '../../../payment/models/paymentOutput';
import { Processors } from '../../../payment/models/processors.interface';
import { PorTransactionService } from '../../../por-transaction.service';
import { LoggerService, NotificationService } from '../../../services';
import { AppFacadeService } from '../../../services/app-facade.service';
import { AppMediatorService } from '../../../services/app-mediator.service';
import { ContractService } from '../../../services/contract.service';
import { InvoicesService } from '../../../services/invoices.service';
import { QuoteStepService } from '../../../services/quote-step.service';
import { selectIsMobile } from '../../app-wide/app/app.selectors';
import { AppState } from '../../app-wide/app/app.state';
import { ConfigActionTypes } from '../../app-wide/config/config.actions';
import { selectConfig, selectCustomerId, selectRouteParams } from '../../app-wide/config/config.selectors';
import { showDialog } from '../../app-wide/dialog/dialog.actions';
import { areContractsValidForMultiPayment, getTotalAmountDueForContracts, selectContractDetail } from '../contract/contract.selectors';
import { convertQuoteToReservation, fetchContractDetailPayment } from '../contract/contract.actions';
import { MINIMUM_PAYMENT_AMOUNT, MULTI_CONTRACT_MAX_SELECTION_ALLOWED, TOKENIZE_CARD_TRANSACTION_TYPE } from '../../../constants/default.const';
import { getCustomer } from '../customer/customer.selectors';
import { closePayment, ContractActionTypes, setSelectedContracts } from './../contract/contract.actions';
import { clearInvoicesinStore, fetchInvoiceTotalDue, InvoiceActionTypes, unsetSelectedInvoices } from '../invoices/invoices.actions';
import { areSelectedInvoicesPayable, getInvoiceFilters } from '../invoices/invoices.selectors';
import { Invoice } from '../../../models/invoice-model';
import {
    createTransaction,
    crossAppEventEmitted,
    failCreateTransaction,
    failLoadPaymentBoundaries,
    failVerifyPayment,
    loadPaymentProcessorAvailability,
    loadPaymentProcessorAvailabilityFailure,
    loadPaymentProcessorAvailabilitySuccess,
    PaymentActionTypes,
    setConfigData,
    setDialogEnabled,
    setMultiPaymentBoundaries,
    setPaymentAppInput,
    setPaymentSectionEnabled,
    setTransactionLoaded,
    successCreateTransaction,
    successVerifyPayment
} from './../payment/payment.actions';
import {
    selectDefaultPaymentMid,
    selectIsPaymentProcessorAvailable,
    selectMultipaymentBoundaries,
    selectPaymentAppOutput,
    selectPaymentSelectedIds,
    selectPaymentSelection,
    PaymentSelection,
    selectPaymentSelectedTotal,
    selectPaymentSubject
} from './payment.selectors';
import { ContractCP } from '../../../models/contract-model';

@Injectable()
export class PaymentEffects {
    private readonly actions$ = inject(Actions);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly translateService = inject(TranslateService);
    private readonly porTransactionService = inject(PorTransactionService);
    private readonly notification = inject(NotificationService);
    private readonly logger = inject(LoggerService);
    private readonly contractService = inject(ContractService);
    private readonly invoicesService = inject(InvoicesService);
    private readonly store = inject<Store<AppState>>(Store);
    private readonly quoteStepService = inject(QuoteStepService);

    /**
     * ADO-104813 AC3 / reservation-step-tracker AC8/AC9: react to a verified payment.
     * `successVerifyPayment` is dispatched on BOTH the success path and the decline/error branch
     * (alongside `failVerifyPayment`), so we filter on TransactionResultEnum to avoid refreshing the
     * list or auto-converting on a failed payment (AC5 — records nothing, status unchanged).
     * - AC3: re-fetch the payment history so the accordion updates without a page reload.
     * - AC8/AC9: when the paid contract is a Quote, dispatch conversion only once ALL required steps
     *   (Payment, eSign) are complete — not just payment — via the shared QuoteStepService. The
     *   convertQuoteToReservation$ effect already re-checks the `autoConvertQuotes` flag.
     */
    handlePaymentSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(successVerifyPayment),
            filter(({ lastPaymentDetail }) => {
                const result = lastPaymentDetail?.TransactionData?.TransactionResultEnum;
                return result === TransactionResultEnum.APPROVED || result === TransactionResultEnum.APPROVEDWITHWARNING;
            }),
            withLatestFrom(
                this.store.select(selectCustomerId),
                this.store.select(selectPaymentSelectedIds),
                this.store.select(selectContractDetail),
                this.store.select(selectPaymentSubject),
                this.store.select(selectMultipaymentBoundaries)
            ),
            switchMap(([, customerId, selectedContracts, contractDetail, paymentSubject, priorBoundaries]) => {
                // ADO-116161/ADO-116163: contract-only effect — invoice payments must not refresh
                // contract history or convert a Quote (AC8: no Quote-to-Reservation, no contract
                // payment-history refresh for an invoice payment). Deliberate no-op, not deferred scope.
                if (paymentSubject !== 'contract') {
                    return [];
                }
                // Neither the action nor its payload carries the contract id; derive it from the
                // contract(s) just paid (cleared synchronously after this action, so still present here).
                const contractId = selectedContracts?.[0] ?? contractDetail?.Id ?? '';
                if (!contractId) {
                    return [];
                }
                const refreshHistory = fetchContractDetailPayment({ customerId, contractId });
                // Auto-conversion only applies to Quotes; skip the extra step-status check otherwise.
                if (contractDetail?.ContractStatus !== ContractStatusEnum.Quote) {
                    return [refreshHistory];
                }
                // WHY: contractId above can come from selectedContracts[0] (a multi-contract payment
                // selection), while contractDetail is a separate store slice that isn't guaranteed to be
                // the same contract — e.g. it could still hold whatever contract was last opened in the
                // UI. resolveStepStatus takes EsignUrl from contractDetail to decide whether to fetch the
                // eSign state, so trusting it here when the ids disagree could skip or run that fetch based
                // on a different contract's eSign request and auto-convert contractId on the wrong basis.
                // HOW: only run the eSign-aware auto-convert check when contractDetail actually IS
                // contractId. When they diverge, skip auto-convert for this event rather than guess —
                // recheckQuoteCompletionOnDetailLoad$ (contract.effects.ts) will still catch a legitimate
                // completion the next time the consumer views this contract with fresh, matching data.
                if (contractDetail?.Id !== contractId) {
                    return [refreshHistory];
                }
                // ADO-111669: use the pre-payment store snapshot (loaded once at payment start) as the
                // staleness baseline for the post-payment boundary read. If the minimum was already met
                // BEFORE this payment it stays met (a payment only adds money), so no re-fetch is needed;
                // otherwise resolveStepStatus polls until the boundary actually reflects this payment.
                const previousMinAmount = priorBoundaries?.length ? Math.min(...priorBoundaries.map(boundary => boundary.minAmount)) : null;
                // Decide completion (including a FRESH, post-payment payment boundary — the same
                // authoritative source the completion badge uses) via the shared QuoteStepService so
                // an eSign-required-and-incomplete Quote isn't converted just because payment cleared.
                // Gate on allRequiredStepsComplete rather than shouldAutoConvert: a payment just happened
                // on THIS path, so the "nothing required, so don't vacuously auto-convert" concern behind
                // shouldAutoConvert (see quote-step.service.ts) doesn't apply here — it would otherwise
                // silently withdraw payment-triggered conversion for orgs with neither step marked required.
                return this.quoteStepService.resolveStepStatus(customerId, contractId, contractDetail, previousMinAmount).pipe(
                    switchMap(status => (status.allRequiredStepsComplete ? [refreshHistory, convertQuoteToReservation({ customerId, contractId })] : [refreshHistory])),
                    catchError(() => of(refreshHistory))
                );
            })
        )
    );

    /**
     * Load payment boundaries based on selected contracts
     * - Single contract: GET /get-boundary/{contractId} (direct, faster, includes proper MID)
     * - Multiple contracts: POST /get-multi-boundary (loop, aggregate)
     * This ensures single contract payments don't show unnecessary MID dropdown
     */
    loadPaymentBoundaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActionTypes.MultiPaymentContinue, PaymentActionTypes.StartPayment, InvoiceActionTypes.InvoiceMultiPaymentContinue),
            tap(() => {
                this.appFacadeService.setLoading(true);
            }),
            withLatestFrom(this.store.select(selectPaymentSelectedIds)),
            withLatestFrom(this.store.select(selectPaymentSelection)),
            withLatestFrom(this.store.select(selectPaymentSelectedTotal)),
            withLatestFrom(this.store.select(selectCustomerId)),
            withLatestFrom(this.store.select(selectRouteParams)),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            switchMap(([[[[[_action, selectedContracts], paymentSelection], total], customerId], _routeParams]) => {
                // ADO-116163: invoices bypass GAPI entirely — no contract eligibility rules, no
                // boundary endpoint call. The synthesized boundary is "pay in full" (min = max =
                // total); onSetMultiboundaries$ still applies the single-item $1 floor clamp for a
                // single-invoice selection, same as it already does for a single contract.
                if (paymentSelection.subject === 'invoice') {
                    return this.store.select(areSelectedInvoicesPayable).pipe(
                        take(1),
                        map(isPayable => {
                            this.appFacadeService.setLoading(false);
                            if (!isPayable) {
                                this.notification.error(this.translateService.instant('MultiContractTotalMustBePositive', { total }));
                                return failLoadPaymentBoundaries({
                                    error: `Cannot process payment. Total AmountDue is $${total}. Must be greater than $0.`
                                });
                            }
                            const paymentBoundaries: PaymentMinMaxBoundary[] = [
                                {
                                    paymentMethod: PaymentMethodEnums.CreditCard,
                                    minAmount: total,
                                    maxAmount: total,
                                    id: selectedContracts[0],
                                    name: 'Invoice Payment'
                                }
                            ];
                            return setMultiPaymentBoundaries({ paymentBoundaries });
                        })
                    );
                }

                const contractModels: ContractCP[] = paymentSelection.subject === 'contract' ? paymentSelection.models : [];
                const isSingleContract = selectedContracts.length === 1;

                // Multi-contract validation (using selector projectors)
                if (!isSingleContract) {
                    // Validate: Max 50 contracts
                    if (selectedContracts.length > MULTI_CONTRACT_MAX_SELECTION_ALLOWED) {
                        this.appFacadeService.setLoading(false);
                        this.notification.error(
                            this.translateService.instant('MaximumContractsExceeded', {
                                max: MULTI_CONTRACT_MAX_SELECTION_ALLOWED,
                                provided: selectedContracts.length
                            })
                        );
                        return of(
                            failLoadPaymentBoundaries({
                                error: `Maximum ${MULTI_CONTRACT_MAX_SELECTION_ALLOWED} contracts allowed. Selected ${selectedContracts.length} contracts.`
                            })
                        );
                    }

                    // Validate: Contracts eligible using selector
                    const validation = areContractsValidForMultiPayment(selectedContracts).projector(contractModels);
                    if (!validation.valid) {
                        this.appFacadeService.setLoading(false);
                        this.notification.error(
                            this.translateService.instant('InvalidContractsForMultiPayment', {
                                invalid: validation.invalidCount,
                                total: selectedContracts.length,
                                contractIds: validation.invalidContracts.map(c => c.contractId).join(', ')
                            })
                        );
                        return of(
                            failLoadPaymentBoundaries({
                                error: `${validation.invalidCount} contracts not eligible (must be Closed status with balance > 0, no pending payment)`
                            })
                        );
                    }

                    // Validate: Total AmountDue > 0 using selector
                    const totalDue = getTotalAmountDueForContracts(selectedContracts).projector(contractModels);
                    if (totalDue <= 0) {
                        this.appFacadeService.setLoading(false);
                        this.notification.error(
                            this.translateService.instant('MultiContractTotalMustBePositive', {
                                total: totalDue
                            })
                        );
                        return of(
                            failLoadPaymentBoundaries({
                                error: `Cannot process payment. Total AmountDue is $${totalDue}. Must be greater than $0.`
                            })
                        );
                    }

                    // Multi-contract: ALWAYS uses sum of AmountDue (ignore routeParams.amount)
                    // No amount validation needed - system calculates automatically

                    // All validations passed - proceed with multi-boundary API call
                    return this.appMediatorService.porPayService.getPaymentBoundaryMultiContract(customerId, selectedContracts, total).pipe(
                        map((paymentBoundaries: PaymentMinMaxBoundary[]) => {
                            this.appFacadeService.setLoading(false);
                            return setMultiPaymentBoundaries({ paymentBoundaries });
                        }),
                        catchError(error => {
                            this.appFacadeService.setLoading(false);
                            return of(failLoadPaymentBoundaries({ error: error.message }));
                        })
                    );
                }

                // Single contract: Use direct endpoint (no validation needed)
                const contractId = selectedContracts[0];
                return this.appMediatorService.porPayService.getMinMaxFromPaymentBoundary(customerId, contractId).pipe(
                    map((paymentBoundaries: PaymentMinMaxBoundary[]) => {
                        this.appFacadeService.setLoading(false);
                        return setMultiPaymentBoundaries({ paymentBoundaries });
                    }),
                    catchError(error => {
                        this.appFacadeService.setLoading(false);
                        return of(failLoadPaymentBoundaries({ error: error.message }));
                    })
                );
            })
        )
    );

    onSetMultiboundaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.SetMultiPaymentBoundaries),
            withLatestFrom(this.store.select(selectMultipaymentBoundaries)),
            withLatestFrom(this.store.select(selectPaymentSelectedIds)),
            withLatestFrom(this.store.select(selectRouteParams)),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            switchMap(([[[_action, paymentBoundaries], selectedContracts], routeParams]) => {
                if (paymentBoundaries) {
                    const isSingleContract = selectedContracts.length === 1;
                    let amount: string | null = null;

                    /**
                     * ADO-119182 scope decision (PR 30867 thread 317513): the client floor below
                     * is single-contract ONLY, deliberately. For a multi-contract selection
                     * `amount` stays null and createTransaction$ resolves null to the selected
                     * total, so a sub-floor total (e.g. two contracts at $0.40) does dispatch.
                     *
                     * Enforcement for that path is server-side: payment.service.ts rejects
                     * Amount_Requested < MINIMUM_PAYMENT_AMOUNT_CENTS unconditionally on the
                     * non-tokenize path, so no money moves. The cost is an asymmetry - single
                     * fails closed in the client with the submit control disabled, multi comes
                     * back as the generic CreateTransactionFailed.
                     *
                     * The floor is NOT added to createTransaction$: that effect is the single
                     * funnel for every payment type, including the tokenize/save-card flow the
                     * server exempts (it substitutes TOKENIZE_ONLY_AMOUNT_CENTS), and the action
                     * carries no tokenize flag to distinguish them. A blanket guard there could
                     * silently block card saving. If this asymmetry is ever closed, the right
                     * place is the multi-contract `totalDue` guard in loadPaymentBoundaries$,
                     * which already fails closed with user-visible copy.
                     *
                     * Pinned by: 'deliberately does NOT apply the client floor to a
                     * multi-contract total' in payment.effects.spec.ts.
                     */
                    // For single contract, determine amount based on boundaries
                    if (isSingleContract) {
                        // ADO-119182: the boundary minimum is advisory below the hard online floor; RMS can send 0.
                        const minBoundary = Math.max(paymentBoundaries[0]?.minAmount || 0, MINIMUM_PAYMENT_AMOUNT);
                        const maxBoundary = paymentBoundaries[0]?.maxAmount || 0;

                        /**
                         * ADO-119182: a maximum below the floor leaves no payable amount in
                         * [floor, max], so dispatch nothing at all. Do NOT fall through with
                         * `amount = null`: createTransaction$ reads null as "use the selected
                         * total" (`amount ? amount : total`), which would request the full
                         * contract AmountDue - above the boundary maximum.
                         */
                        if (maxBoundary < MINIMUM_PAYMENT_AMOUNT) {
                            return [];
                        }

                        if (minBoundary === maxBoundary && maxBoundary > 0) {
                            amount = maxBoundary.toString();
                        } else {
                            // User has choice (min < max)
                            const providedAmount = routeParams?.amount;

                            if (providedAmount !== undefined && providedAmount !== null) {
                                // Amount provided - validate it's within range
                                if (providedAmount >= minBoundary && providedAmount <= maxBoundary) {
                                    // Valid amount within range - use it
                                    amount = providedAmount.toString();
                                } else {
                                    // Outside range - use maxBoundary
                                    amount = maxBoundary.toString();
                                }
                            } else {
                                // No amount provided - use maxBoundary
                                amount = maxBoundary.toString();
                            }
                        }
                    }

                    return of(
                        createTransaction({
                            amount,
                            transactionType: isSingleContract ? PaymentTransactionType.Single : PaymentTransactionType.Multiple
                        })
                    );
                }
                return of();
            })
        )
    );

    resetPaymentBoundaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContractActionTypes.PaymentWindowClosed, PaymentActionTypes.PaymentWindowClosed),
            map(() => setMultiPaymentBoundaries({ paymentBoundaries: null }))
        )
    );

    setConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ConfigActionTypes.SetConfig),
            map(action => setConfigData({ config: action }))
        )
    );

    createTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.CreateTransaction),
            tap(() => {
                this.appFacadeService.setLoading(true);
            }),
            withLatestFrom(
                this.store.select(selectPaymentSelectedIds),
                this.store.select(selectPaymentSelection),
                this.store.select(selectPaymentSelectedTotal),
                this.store.select(selectConfig),
                this.appFacadeService.getCurrency(),
                this.store.select(selectCustomerId),
                this.store.select(selectIsMobile),
                this.store.select(selectDefaultPaymentMid),
                this.store.select(selectIsPaymentProcessorAvailable)
            ),
            switchMap(([action, selectedContracts, paymentSelection, total, config, currencyCode, customerId, isMobile, defaultPaymentMid, processors]) => {
                // ADO-116163: invoice MID resolution has no invoice depot (Invoice has no DepotId,
                // only DepotName) — selectedContractsModels stays [] for the invoice subject, so
                // getPaymentMid's firstContractRemoteDepotId argument falls through to its
                // multipay/default MID resolution below.
                const selectedContractsModels: ContractCP[] = paymentSelection.subject === 'contract' ? paymentSelection.models : [];
                const isInvoicePayment = paymentSelection.subject === 'invoice';
                const { amount, transactionType } = action;
                const toPay = amount ? amount : total ? total.toString() : '0';
                const amountToPay: string = this.appMediatorService.porPayService.formatCurrencyForPorPay(toPay);
                setTransactionLoaded({ transactionLoaded: false });
                // ADO-116163: guard the average against divide-by-zero for an empty selection
                // (parseFloat(toPay) / 0 would resolve to NaN).
                const averageValue = selectedContracts.length ? parseFloat(toPay) / selectedContracts.length : 0;
                const metadata: PaymentMetaData = isInvoicePayment
                    ? {
                          source: this.getPaymentSource(config),
                          actionType: transactionType,
                          averageInvoiceValue: averageValue,
                          deviceType: isMobile ? DeviceType.Mobile : DeviceType.Desktop,
                          paymentAllocation: selectedContracts.length,
                          customerId: customerId,
                          invoiceIds: selectedContracts.join(',')
                      }
                    : {
                          source: this.getPaymentSource(config),
                          actionType: transactionType,
                          averageContractValue: averageValue,
                          deviceType: isMobile ? DeviceType.Mobile : DeviceType.Desktop,
                          paymentAllocation: selectedContracts.length,
                          customerId: customerId,
                          contractIds: selectedContracts.join(',')
                      };
                const paymentMid = this.appMediatorService.porPayService.getPaymentMid(selectedContractsModels[0]?.depotId, selectedContracts, processors, defaultPaymentMid);

                return this.appMediatorService.porPayService
                    .createPayment(
                        customerId,
                        currencyCode,
                        Number(amountToPay),
                        config.organizationId ?? '',
                        paymentMid,
                        isInvoicePayment ? [] : selectedContracts,
                        Number(amountToPay),
                        metadata,
                        isInvoicePayment ? selectedContracts : undefined
                    )
                    .pipe(
                        switchMap((res: unknown) => {
                            this.appFacadeService.setLoading(false);
                            return of(successCreateTransaction({ res }));
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return [
                                setTransactionLoaded({ transactionLoaded: true }),
                                failCreateTransaction({ error: [this.translateService.instant('CreateTransactionFailed'), error?.message, error?.error?.token, error?.error?.message].join(' / ') }),
                                setDialogEnabled({ dialogEnabled: true }),
                                crossAppEventEmitted({
                                    event: {
                                        error: {
                                            code: error?.status,
                                            message: error?.error?.message ?? error?.message
                                        }
                                    }
                                })
                            ];
                        })
                    );
            })
        )
    );

    successTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.SuccessCreateTransaction),
            switchMap((action: { type: string; res: unknown }) => {
                const response: CreateTransactionResponse = action.res as CreateTransactionResponse;
                if (response.token && response.token === 'NO_PAYMENT_METHOD_AVAILABLE') {
                    return [
                        setTransactionLoaded({ transactionLoaded: true }),
                        failCreateTransaction({ error: this.translateService.instant('CreateTransactionFailed') + this.translateService.instant('NoPaymentMethodAvailable') }),
                        setDialogEnabled({ dialogEnabled: true })
                    ];
                }

                return this.appMediatorService.porPayService.getPaymentConfiguration(response?.accessToken, response?.transactionId).pipe(
                    switchMap(configuration => {
                        return [setPaymentAppInput({ paymentAppInput: JSON.stringify(configuration) }), setTransactionLoaded({ transactionLoaded: true })];
                    })
                );
            })
        )
    );

    paymentFinished$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PaymentActionTypes.PaymentFinished),
            withLatestFrom(this.store.select(selectPaymentSelection)),
            withLatestFrom(this.store.select(getCustomer)),
            withLatestFrom(this.store.select(selectConfig)),
            withLatestFrom(this.store.select(selectPaymentAppOutput)),
            withLatestFrom(this.store.select(selectCustomerId)),
            withLatestFrom(this.store.select(selectRouteParams)),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            switchMap(([[[[[[_action, paymentSelection], customer], config], paymentAppOutput], customerId], routeParams]) => {
                const selectedContractsModels: ContractCP[] = paymentSelection.subject === 'contract' ? paymentSelection.models : [];
                const selectedInvoiceModels: Invoice[] = paymentSelection.subject === 'invoice' ? paymentSelection.models : [];
                this.logger.logInfo(paymentAppOutput?.detail);
                const detail: PaymentAppPayOutput = paymentAppOutput?.detail as PaymentAppPayOutput;
                if (!detail?.TransactionId) {
                    return [
                        failVerifyPayment({ error: this.translateService.instant('MissingTransactionId') }),
                        setDialogEnabled({ dialogEnabled: true }),
                        crossAppEventEmitted({
                            event: {
                                error: {
                                    code: 400,
                                    message: this.translateService.instant('MissingTransactionId')
                                }
                            }
                        })
                    ];
                }
                if (
                    detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.APPROVED ||
                    detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.APPROVEDWITHWARNING ||
                    detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.PENDING
                ) {
                    this.notification.success(detail?.Message);
                    /**
                     * A tokenize captured nothing, so "verify the received payment" would tell the
                     * customer money moved (AB#114949). Same TransactionTypeEnum check as
                     * withoutTokenizeSentinelAmount below.
                     */
                    const isTokenizeTransaction = detail?.TransactionData?.TransactionTypeEnum === TOKENIZE_CARD_TRANSACTION_TYPE;
                    this.notification.success(this.translateService.instant(isTokenizeTransaction ? 'PleaseWaitWhileWeSaveYourCard' : 'Please wait till we verify the received payment') + '.');

                    this.appFacadeService.setLoading(true);
                    const paymentSource = this.getPaymentSource(config);
                    const paymentData: PaymentData = this.appMediatorService.porPayService.getPaymentData(
                        customer,
                        config.organizationId ?? '',
                        detail,
                        selectedContractsModels,
                        paymentSource,
                        config,
                        selectedInvoiceModels
                    );

                    return this.appMediatorService.porPayService.verifyPayment(customerId, paymentData).pipe(
                        switchMap((res: VerifyTransaction) => {
                            this.logger.logWarning(res);
                            return this.verifiedPaymentActions(paymentSelection, customerId, routeParams, paymentData, selectedInvoiceModels, detail, res.data ?? detail, false);
                        }),
                        catchError(error => {
                            this.logger.logError(error, '', true);
                            // POR Pay already took the payment, so a 5xx or no response (offline too) is a relay failure:
                            // an error here would invite a second payment
                            if (error?.status === 0 || error?.status >= 500) {
                                return this.verifiedPaymentActions(paymentSelection, customerId, routeParams, paymentData, selectedInvoiceModels, detail, detail, true);
                            }
                            this.appFacadeService.setLoading(false);
                            return [
                                failVerifyPayment({ error: error?.message || this.translateService.instant('errorMessage') }),
                                setDialogEnabled({ dialogEnabled: true }),
                                crossAppEventEmitted({
                                    event: {
                                        error: {
                                            code: error?.status,
                                            message: error?.message || this.translateService.instant('errorMessage')
                                        }
                                    }
                                })
                            ];
                        })
                    );
                }

                let errorMessage = this.translateService.instant('PaymentTransactionErroredMessage');
                if (detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.CANCELLED) {
                    errorMessage = this.translateService.instant('PaymentTransactionCancelledMessage');
                } else if (detail?.TransactionData?.TransactionResultEnum === TransactionResultEnum.DECLINED) {
                    errorMessage = this.translateService.instant('PaymentTransactionDeclinedMessage');
                }
                return [
                    successVerifyPayment({ lastPaymentDetail: detail }),
                    failVerifyPayment({ error: errorMessage }),
                    setPaymentSectionEnabled({ enabled: false }),
                    showDialog({ dialogContentType: DialogContentType.PaymentSuccess }),
                    crossAppEventEmitted({
                        event: {
                            error: {
                                code: 400,
                                message: errorMessage
                            }
                        }
                    }),
                    closePayment()
                ];
            })
        )
    );

    loadPaymentProcessorAvailability$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadPaymentProcessorAvailability),
            switchMap(({ customerId }) =>
                this.appMediatorService.porPayService.isPaymentProcessorAvailable(customerId).pipe(
                    map((processors: boolean | Processors[]) => loadPaymentProcessorAvailabilitySuccess({ processors })),
                    catchError(() => of(loadPaymentProcessorAvailabilityFailure()))
                )
            )
        )
    );

    /**
     * The post-verify completion actions, shared by a confirmed verify and a relay failure after POR Pay took the payment.
     */
    private verifiedPaymentActions(
        paymentSelection: PaymentSelection,
        customerId: string,
        routeParams: ReturnType<typeof selectRouteParams>,
        paymentData: PaymentData,
        selectedInvoiceModels: Invoice[],
        detail: PaymentAppPayOutput,
        lastPaymentDetail: PaymentAppPayOutput,
        confirmationPending: boolean
    ) {
        this.appFacadeService.setLoading(false);
        this.porTransactionService.setTransactionSuccess(true);
        // ADO-116161/ADO-116163: the contract list refresh/selection-clear and the
        // invoice list refresh/selection-clear are subject-domain side effects and
        // must not fire for the other subject.
        const isContractPayment = paymentSelection.subject === 'contract';
        const isInvoicePayment = paymentSelection.subject === 'invoice';
        if (isContractPayment && routeParams === undefined) {
            this.contractService.loadContracts({ customerId, search: paymentData?.contractId ?? paymentData?.contractIds?.join(',') });
        } else if (isInvoicePayment && routeParams === undefined) {
            // ADO-116163: refresh the invoice list so the paid invoice reflects its
            // updated AmountDue without a manual reload. Clear the store first - resetting
            // pageNumber to 1 and dropping the stale appended rows - then re-fetch a fresh
            // page, exactly as InvoicesService.filter() does. The previous call passed the
            // paid invoice *ids* as the InvoiceNumber *search* term and appended an empty
            // page to a paged list (which also hid Load More), leaving the paid invoice
            // still showing as owed.
            // ADO-116163: re-fetch with the user's active filters (persisted in the
            // invoices store) rather than unfiltered, so paying doesn't silently drop
            // the filter the UI still shows. clearInvoicesinStore leaves filters intact.
            this.store.dispatch(clearInvoicesinStore());
            this.store
                .select(getInvoiceFilters)
                .pipe(take(1))
                .subscribe(invoiceFilters => {
                    this.invoicesService.getInvoices(customerId, invoiceFilters?.startDate, invoiceFilters?.endDate, invoiceFilters?.search, invoiceFilters?.status);
                });
            // ADO-116163: also refresh the "Total Due" header so it reflects the
            // updated amount due after payment, not just the invoice rows.
            this.store.dispatch(fetchInvoiceTotalDue({ customerId }));
        }
        return [
            successVerifyPayment({ lastPaymentDetail, confirmationPending }),
            closePayment(),
            setPaymentSectionEnabled({ enabled: false }),
            ...(isContractPayment ? [setSelectedContracts({ selectedContracts: [] })] : []),
            ...(isInvoicePayment ? [unsetSelectedInvoices({ unselectInvoices: selectedInvoiceModels.map(invoice => invoice.Id) })] : []),
            showDialog({ dialogContentType: DialogContentType.PaymentSuccess }),
            crossAppEventEmitted({
                event: {
                    response: this.withoutTokenizeSentinelAmount(detail)
                }
            })
        ];
    }

    /**
     * Strip the tokenize-only sentinel amount before the transaction leaves for the host page.
     *
     * A TokenizeCard transaction is sent to POR-Pay with a 100-cent sentinel because POR-Pay
     * rejects Amount_Requested = 0, but nothing is ever captured. The API already reports 0 to
     * Elite (payment.service.ts), so without this the host page would be the only consumer told
     * $1 was processed - and a provider reading Amount_Processed would record a $1 payment.
     * @param detail - the POR-Pay pay output, passed through untouched for a normal Purchase
     */
    private withoutTokenizeSentinelAmount(detail: PaymentAppPayOutput): PaymentAppPayOutput {
        if (detail?.TransactionData?.TransactionTypeEnum !== TOKENIZE_CARD_TRANSACTION_TYPE) {
            return detail;
        }

        return {
            ...detail,
            /* eslint-disable-next-line @typescript-eslint/naming-convention */
            TransactionData: { ...detail.TransactionData, Amount_Processed: 0 }
        };
    }

    private getPaymentSource(config: { sourceApp?: string }): PaymentSourceEnums {
        let paymentSource = PaymentSourceEnums.ConsumerPortal;

        if (config.sourceApp?.length) {
            paymentSource = config.sourceApp as PaymentSourceEnums;
        } else if (this.appFacadeService.versionToggleService.isQuickLinkVersion()) {
            paymentSource = PaymentSourceEnums.QuickLink;
        }

        return paymentSource;
    }
}
